import {
  PII_CUSTOM_ENTITIES_QUERY_KEY,
  PII_CUSTOM_ENTITY_LABELS_QUERY_KEY,
} from "@frontend-shared/constants";
import type {
  ICreatePiiCustomEntityParams,
  IDeletePiiCustomEntityParams,
  IPiiCustomEntity,
  IPiiCustomEntityLabel,
  IUpdatePiiCustomEntityParams,
} from "@frontend-shared/types";
import type {
  QueryClient,
  UseMutationResult,
  UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { piiApi } from "@/api/services";

interface IInvalidateCustomEntityQueriesProps {
  queryClient: QueryClient;
  organizationUuid: string;
}

// NOTE: NSAI-6787 — a create / update / delete changes both the list and the
// placeholder-type → name labels chips and summaries read, so both refetch.
const invalidateCustomEntityQueries = async ({
  queryClient,
  organizationUuid,
}: IInvalidateCustomEntityQueriesProps): Promise<void> => {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: [PII_CUSTOM_ENTITIES_QUERY_KEY, organizationUuid],
    }),
    queryClient.invalidateQueries({
      queryKey: [PII_CUSTOM_ENTITY_LABELS_QUERY_KEY, organizationUuid],
    }),
  ]);
};

// NOTE: The section renders every refusal inline — translated from the
// server's errorCode — so the global mutation toast would say it twice.
const CUSTOM_ENTITY_MUTATION_META = { suppressErrorToast: true } as const;

export const useGetCustomEntities = (
  organizationUuid: string | undefined,
): UseQueryResult<IPiiCustomEntity[]> => {
  return useQuery({
    queryKey: [PII_CUSTOM_ENTITIES_QUERY_KEY, organizationUuid],
    queryFn: () =>
      piiApi.listCustomEntities({
        organizationUuid: organizationUuid ?? "",
      }),
    enabled: !!organizationUuid,
  });
};

interface IUseListPiiCustomEntityLabelsProps {
  organizationUuid: string | null | undefined;
  isEnabled: boolean;
}

// NOTE: NSAI-6787 — any member may read these: they only map each custom
// entity's placeholder type (CUSTOM_EMPLOYEE_ID) to its display name, so a chip
// never shows the raw identifier. The endpoint 404s while the custom-entities
// flag is off, hence `isEnabled`.
export const useListPiiCustomEntityLabels = ({
  organizationUuid,
  isEnabled,
}: IUseListPiiCustomEntityLabelsProps): UseQueryResult<
  IPiiCustomEntityLabel[]
> => {
  return useQuery({
    queryKey: [PII_CUSTOM_ENTITY_LABELS_QUERY_KEY, organizationUuid],
    queryFn: () =>
      piiApi.listCustomEntityLabels({
        organizationUuid: organizationUuid ?? "",
      }),
    enabled: isEnabled && !!organizationUuid,
  });
};

export const useCreateCustomEntity = (): UseMutationResult<
  IPiiCustomEntity,
  Error,
  ICreatePiiCustomEntityParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: ICreatePiiCustomEntityParams) =>
      piiApi.createCustomEntity(params),
    onSuccess: async (_data, { organizationUuid }) => {
      await invalidateCustomEntityQueries({ queryClient, organizationUuid });
    },
    meta: CUSTOM_ENTITY_MUTATION_META,
  });
};

export const useUpdateCustomEntity = (): UseMutationResult<
  IPiiCustomEntity,
  Error,
  IUpdatePiiCustomEntityParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: IUpdatePiiCustomEntityParams) =>
      piiApi.updateCustomEntity(params),
    onSuccess: async (_data, { organizationUuid }) => {
      await invalidateCustomEntityQueries({ queryClient, organizationUuid });
    },
    meta: CUSTOM_ENTITY_MUTATION_META,
  });
};

export const useDeleteCustomEntity = (): UseMutationResult<
  void,
  Error,
  IDeletePiiCustomEntityParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: IDeletePiiCustomEntityParams) =>
      piiApi.deleteCustomEntity(params),
    onSuccess: async (_data, { organizationUuid }) => {
      await invalidateCustomEntityQueries({ queryClient, organizationUuid });
    },
    meta: CUSTOM_ENTITY_MUTATION_META,
  });
};
